/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable react/jsx-one-expression-per-line */
import React, {
  useContext, useEffect, useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AlertTriangle, HelpCircle, X, ArrowLeft } from 'lucide-react';
import PropTypes from 'prop-types';
import { Navbar, Sidebar } from '../../components';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import API from '../../api/endpoints';
import DateRangePicker from '../../components/dateRangePicker';
import './analytics.css';
import { UnusualActivityTypes } from '../../constants/unusualActivityTypes';
import DataTable from '../../components/DataTable';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import ScreenshotViewer from '../viewActivity/ScreenshotViewer';
import {
  getEvidenceForDetectionType,
  getEvidenceUrlKey,
  getVisualEvidenceCount,
} from './unusualActivityEvidence';

const EvidenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-top: 8px;
`;

const EvidenceThumbnail = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #E0E1E6;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }
`;

const EvidenceBadge = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  background: ${(props) => (props.type === 'webcam' ? '#213D69' : '#6B7280')};
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  text-transform: uppercase;
`;

const EvidenceTimestamp = styled.div`
  font-size: 11px;
  color: #6B7280;
  padding: 4px 6px;
  text-align: center;
`;

const EvidenceEmptyState = styled.div`
  font-size: 13px;
  color: #9CA3AF;
  padding: 12px;
  text-align: center;
  background: #F9FAFB;
  border-radius: 6px;
`;

// Risk level colors for consistency
const RISK_COLORS = {
  critical: '#dc3545',
  high: '#dc3545',
  medium: '#ffc107',
  low: '#28a745',
  none: '#6c757d',
};

const getRiskColor = (riskLevel) => RISK_COLORS[riskLevel] || RISK_COLORS.none;

const TIMEZONES = moment.tz.names();

const CONFIDENCE_TONE = {
  high: { bg: '#FCECE7', fg: '#CC4525' },
  medium: { bg: '#FDF1E3', fg: '#B3650F' },
  low: { bg: '#E7F6E6', fg: '#256D23' },
};

function ConfidenceBadge({ value }) {
  const tone = CONFIDENCE_TONE[String(value || '').toLowerCase()] || CONFIDENCE_TONE.low;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style={{ background: tone.bg, color: tone.fg }}>
      {value}
    </span>
  );
}

ConfidenceBadge.propTypes = { value: PropTypes.string };

function StatCard({ title, value, subTitle = '' }) {
  return (
    <div className="flex-1 bg-white border border-rule rounded-[12px] px-4 py-3" style={{ borderStyle: 'solid', minWidth: 180 }}>
      <p className="text-xs font-medium text-ink-500 mb-1">{title}</p>
      <p className="text-2xl font-semibold text-ink-900 tabular-nums">{value}</p>
      {subTitle ? <p className="text-xs text-ink-500 mt-1">{subTitle}</p> : null}
    </div>
  );
}

StatCard.propTypes = {
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subTitle: PropTypes.string,
};

/**
 * Evidence gallery component showing webcam captures and screenshots
 * from unusual activity evidence collection
 */
function EvidenceSection({
  evidence,
  evidenceUrls,
  onThumbnailClick,
  detectionType,
  eventIdsByType,
}) {
  if (!evidence || evidence.length === 0) return null;

  const filteredEvidence = getEvidenceForDetectionType(evidence, detectionType, eventIdsByType);
  const visualEvidence = filteredEvidence.filter((ev) => ev.type !== 'popup_response');
  const popupResponses = filteredEvidence.filter((ev) => ev.type === 'popup_response');

  if (visualEvidence.length === 0 && popupResponses.length === 0) return null;

  return (
    <div className="d-flex flex-column gap-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
        Evidence (
        {visualEvidence.length}
        {' '}
        {visualEvidence.length === 1 ? 'capture' : 'captures'}
        )
      </div>

      {popupResponses.length > 0 && (
        <div style={{
          fontSize: '12px', color: '#28a745', padding: '4px 8px', background: '#f0fdf4', borderRadius: '4px',
        }}
        >
          ✓ User confirmed &quot;Still working&quot; at
          {' '}
          {moment(popupResponses[0].capturedAt).format('h:mm a')}
        </div>
      )}

      {visualEvidence.length > 0 ? (
        <EvidenceGrid>
          {visualEvidence.map((ev) => {
            const evidenceKey = getEvidenceUrlKey(ev);
            const url = evidenceUrls[evidenceKey];
            return (
              <EvidenceThumbnail
                key={ev.id || ev.fileName}
                onClick={() => url && onThumbnailClick(url)}
                /* eslint-disable-next-line max-len */
                title={`${ev.type === 'webcam' ? 'Webcam' : 'Screenshot'} — ${moment(ev.capturedAt).format('h:mm:ss a')}`}
              >
                {url ? (
                  <img src={url} alt={ev.type} loading="lazy" />
                ) : (
                  <div style={{
                    // eslint-disable-next-line max-len
                    width: '100%', height: '100px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#9CA3AF',
                  }}
                  >
                    Loading...
                  </div>
                )}
                <EvidenceBadge type={ev.type}>
                  {ev.type === 'webcam' ? '📷' : '🖥️'}
                  {' '}
                  {ev.type}
                </EvidenceBadge>
                <EvidenceTimestamp>
                  {moment(ev.capturedAt).format('h:mm a')}
                  {' — '}
                  {ev.triggerReason?.replace(/_/g, ' ') || ''}
                </EvidenceTimestamp>
              </EvidenceThumbnail>
            );
          })}
        </EvidenceGrid>
      ) : (
        <EvidenceEmptyState>No visual evidence captured</EvidenceEmptyState>
      )}
    </div>
  );
}

EvidenceSection.propTypes = {
  evidence: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    fileName: PropTypes.string,
    filePath: PropTypes.string,
    capturedAt: PropTypes.string,
    triggerReason: PropTypes.string,
    detectionEventId: PropTypes.number,
  })),
  evidenceUrls: PropTypes.objectOf(PropTypes.string),
  onThumbnailClick: PropTypes.func,
  detectionType: PropTypes.string,
  eventIdsByType: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)),
};

function UnusualActivityMember() {
  const { memberId } = useParams();
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [member, setMember] = useState(null);
  const [instances, setInstances] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace.timezone);
  const [date, setDate] = useState(() => {
    const initialToday = moment.tz(selectedTimezone).toDate();
    return [moment.tz(selectedTimezone).subtract(6, 'days').toDate(), initialToday];
  });
  const [summary, setSummary] = useState({
    totalMembers: 0, totalInstances: 0, totalTime: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [openModal, setOpenModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(0);
  const [screenshotsAreOpen, setScreenshotsAreOpen] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [evidenceUrls, setEvidenceUrls] = useState({}); // { evidenceKey: presignedUrl }
  const [selectedEvidenceUrl, setSelectedEvidenceUrl] = useState(null); // For lightbox
  const [showAbout, setShowAbout] = useState(false);

  const selectedInstanceData = (instances && instances[selectedInstance]) || {};
  const startDate = moment(selectedInstanceData.timeSlot || selectedInstanceData.start);
  const endDate = selectedInstanceData.end
    ? moment(selectedInstanceData.end)
    : startDate.isValid() ? startDate.clone().add(selectedInstanceData.duration || 0, 'seconds') : moment();
  const duration = selectedInstanceData.duration || 0;
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const screenshots = selectedInstanceData?.screenshots || [];
  const screenshotCount = screenshots.length;
  const instanceEvidence = selectedInstanceData?.evidence || [];

  // Format time for screenshot viewer
  const formattedTime = duration < 60
    ? `${duration} seconds`
    : `${hours}hr ${minutes}m`;

  const fetchMemberActivity = async () => {
    setIsLoading(true);

    // Convert dates to UTC using selected timezone
    const startOfDay = moment.tz(date[0], selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(date[1], selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const { success, data: res } = await sendRequest({
      method: 'get',
      url: API.UNUSUAL_ACTIVITY.getMemberDetails(memberId),
      params: {
        workspaceId: activeWorkspace.id,
        startDate: startOfDay,
        endDate: endOfDay,
        timezone: selectedTimezone,
      },
      timeout: 30000,
    });

    const { data } = res ?? { success: false, data: { members: [] } };

    if (success) {
      setMember(data.member);
      setInstances(data.instances);
      setSummary(data.summary);
    } else {
      displayNotification({
        message: 'There was an issue fetching member unusual activity. Please try again',
        severity: 'error',
      });
    }
    setIsLoading(false);
  };

  const fetchMembers = async () => {
    // Convert dates to UTC using selected timezone
    const startOfDay = moment.tz(date[0], selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(date[1], selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const { success, data: res } = await sendRequest({
      method: 'get',
      url: API.UNUSUAL_ACTIVITY.getWorkspaceSummary(activeWorkspace.id),
      params: {
        startDate: startOfDay,
        endDate: endOfDay,
        timezone: selectedTimezone,
      },
      timeout: 30000,
    });

    if (success && res?.data?.members) {
      setMembers(res.data.members);
    }
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, activeWorkspace, selectedTimezone]);

  useEffect(() => {
    if (memberId && activeWorkspace?.id) {
      fetchMemberActivity();
      setEvidenceUrls({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, date, activeWorkspace, selectedTimezone]);

  // Fetch cover image when screenshots are available
  useEffect(() => {
    if (screenshots && screenshots.length && memberId && screenshots[0]?.url) {
      const fetchCoverImage = async () => {
        try {
          const { success, data } = await sendRequest({
            method: 'GET',
            url: API.MEMBER.screenshot(memberId),
            params: { baseUrl: screenshots[0].url },
          });

          if (success) {
            setCoverImageUrl(data.url);
          } else {
            console.warn('Failed to fetch cover image for screenshots');
            setCoverImageUrl(null);
          }
        } catch (error) {
          console.error('Error fetching cover image:', error);
          setCoverImageUrl(null);
        }
      };
      fetchCoverImage();
    } else {
      setCoverImageUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshots[0]?.url, memberId]);

  // Resolve evidence file paths to presigned URLs
  useEffect(() => {
    if (!instanceEvidence.length || !memberId) return;

    const resolveEvidenceUrls = async () => {
      const unresolvedItems = instanceEvidence
        .map((ev) => ({
          evidence: ev,
          evidenceKey: getEvidenceUrlKey(ev),
        }))
        .filter(({ evidence, evidenceKey }) => evidence.fileName && evidenceKey && !evidenceUrls[evidenceKey]);

      if (unresolvedItems.length === 0) return;

      const resolvedUrls = {};

      // Resolve each evidence file path
      await Promise.all(unresolvedItems.map(async ({ evidence: ev, evidenceKey }) => {
        try {
          // eslint-disable-next-line max-len
          const filePath = ev.filePath || `evidences/workspace/${activeWorkspace.id}/member/${memberId}/${ev.fileName}`;
          const { success, data } = await sendRequest({
            method: 'GET',
            url: API.MEMBER.screenshot(memberId),
            params: { baseUrl: filePath },
          });
          if (success && data?.url) {
            resolvedUrls[evidenceKey] = data.url;
          }
        } catch (err) {
          console.warn('Failed to resolve evidence URL:', ev.fileName, err);
        }
      }));

      if (Object.keys(resolvedUrls).length > 0) {
        setEvidenceUrls((prev) => ({ ...prev, ...resolvedUrls }));
      }
    };

    resolveEvidenceUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstance, openModal, instanceEvidence.length, memberId, activeWorkspace?.id, evidenceUrls]);

  // const handleOpen = () => {
  //   setOpenModal(true);
  // };

  const handleClose = () => {
    setOpenModal(false);
    setActiveTab(0); // Reset tab when modal closes
  };

  const handleViewInstance = (index) => {
    setSelectedInstance(index);
    setActiveTab(0); // Reset tab when viewing new instance
    setOpenModal(true);
  };

  // Rows carry their index so the View action can open the right instance.
  const instanceRows = (instances || []).map((inst, i) => ({ ...inst, _idx: i }));

  const columns = [
    {
      key: 'dateTime',
      label: 'Date & Time',
      width: 260,
      flex: true,
      render: (inst) => {
        const s = moment(inst.start || inst.timeSlot);
        const e = inst.end ? moment(inst.end) : s.clone().add(inst.duration || 0, 'seconds');
        return <span className="text-ink-900">{`${s.format('ddd, MMM D, YYYY h:mm a')} - ${e.format('h:mm a')}`}</span>;
      },
    },
    {
      key: 'duration',
      label: 'Duration',
      width: 110,
      render: (inst) => {
        const d = inst.duration || 0;
        return <span className="tabular-nums text-ink-700">{`${String(Math.floor(d / 3600)).padStart(2, '0')}:${String(Math.floor((d % 3600) / 60)).padStart(2, '0')}`}</span>;
      },
    },
    { key: 'type', label: 'Type', width: 190, render: (inst) => <span className="text-ink-700">{UnusualActivityTypes[inst.unusualTypeId]?.title || 'Unknown'}</span> },
    { key: 'confidence', label: 'Confidence', width: 120, render: (inst) => <ConfidenceBadge value={inst.confidence || 'Low'} /> },
    { key: 'overall', label: 'Overall Activity', width: 130, render: (inst) => <span className="tabular-nums text-ink-700">{`${inst.overallActivity || 0}%`}</span> },
    {
      key: 'evidence',
      label: 'Evidence',
      width: 100,
      render: (inst) => {
        const c = getVisualEvidenceCount(inst.evidence);
        return c > 0 ? <span className="font-semibold text-brand-700">{`${c} ${c === 1 ? 'file' : 'files'}`}</span> : <span className="text-ink-500">—</span>;
      },
    },
    {
      key: 'action',
      label: '',
      width: 100,
      align: 'right',
      render: (inst) => (
        <button
          type="button"
          onClick={() => handleViewInstance(inst._idx)}
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition"
          style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}
        >
          View
        </button>
      ),
    },
  ];

  const dsLabel = 'text-[11px] font-semibold uppercase tracking-wider text-ink-500';

  // Shared DS body for one detection type (used by both the single + tabbed views).
  const renderDetectionDetail = ({ typeKey, description, riskLevel, confidence, factors }) => {
    const confTone = CONFIDENCE_TONE[String(confidence || '').toLowerCase()];
    return (
      <div className="flex flex-col gap-4">
        {description ? <p className="text-sm text-ink-700 leading-relaxed">{description}</p> : null}

        <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <p className={`${dsLabel} mb-1`}>Date &amp; Time</p>
            <p className="text-sm text-ink-900">{`${startDate.format('ddd, MMM D, YYYY h:mm a')} - ${endDate.format('h:mm a')} (${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`}</p>
          </div>
          <div>
            <p className={`${dsLabel} mb-1`}>Duration</p>
            <p className="text-sm text-ink-900">{`${hours}hr ${minutes}m`}</p>
          </div>
          <div>
            <p className={`${dsLabel} mb-1`}>Risk Level</p>
            <p className="text-sm font-medium" style={{ color: getRiskColor(riskLevel), textTransform: 'capitalize' }}>{riskLevel || 'Unknown'}</p>
          </div>
          <div>
            <p className={`${dsLabel} mb-1`}>Confidence</p>
            {confTone ? <ConfidenceBadge value={confidence} /> : <p className="text-sm text-ink-900">{confidence || 'N/A'}</p>}
          </div>
        </div>

        {factors && factors.length > 0 ? (
          <div>
            <p className={`${dsLabel} mb-2`}>Detection Factors</p>
            <div className="flex flex-col gap-2">
              {factors.map((factor, i) => (
                <div key={factor.id || i} className="rounded-[8px] border border-rule px-3 py-2" style={{ borderStyle: 'solid', background: '#fbfbfc' }}>
                  <p className="text-sm text-ink-900">
                    <span className="font-semibold">{`${factor.factorType || 'Factor'}: `}</span>
                    {factor.issue || 'No description'}
                  </p>
                  {factor.details ? <p className="text-xs text-ink-500 mt-1">{factor.details}</p> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {screenshotCount > 0 ? (
          <div>
            <p className={`${dsLabel} mb-1`}>Screenshots for this instance</p>
            <button type="button" onClick={() => setScreenshotsAreOpen(true)} className="text-sm font-medium text-brand-700 hover:underline bg-transparent border-0 p-0 cursor-pointer">
              {`${screenshotCount} ${screenshotCount === 1 ? 'screenshot' : 'screenshots'}`}
            </button>
          </div>
        ) : null}

        <EvidenceSection
          evidence={instanceEvidence}
          evidenceUrls={evidenceUrls}
          onThumbnailClick={(url) => setSelectedEvidenceUrl(url)}
          detectionType={typeKey}
          eventIdsByType={selectedInstanceData.eventIdsByType}
        />
      </div>
    );
  };

  const memberName = member ? `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() : '';

  return (
    <>
      <Navbar />
      <Sidebar />

      {showAbout && (
        <Modal open onClose={() => setShowAbout(false)} className="flex-row align-c jstfy-c">
          <Box style={{ width: 520, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
            <div className="flex items-center justify-between px-6 pt-5 pb-3">
              <h5 className="inline-flex items-center gap-2 text-lg font-semibold leading-none text-ink-900">
                <AlertTriangle size={18} className="text-[#CC4525]" />
                About Unusual Activity
              </h5>
              <button type="button" aria-label="Close" onClick={() => setShowAbout(false)} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 pt-2 pb-5 flex flex-col gap-4">
              <p className="text-sm text-ink-700 leading-relaxed">
                Unusual Activity flags time periods where a member&apos;s tracked activity deviates from their
                normal pattern. Each row below is a detected instance — open one to see its detection type,
                risk level, contributing factors, and any captured evidence.
              </p>
              <div>
                <h6 className="text-sm font-semibold text-ink-900 mb-2">Confidence levels</h6>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2"><ConfidenceBadge value="High" /><span className="text-sm text-ink-700">Strong deviation — worth reviewing.</span></div>
                  <div className="flex items-center gap-2"><ConfidenceBadge value="Medium" /><span className="text-sm text-ink-700">Moderate deviation from the baseline.</span></div>
                  <div className="flex items-center gap-2"><ConfidenceBadge value="Low" /><span className="text-sm text-ink-700">Minor deviation — usually expected variation.</span></div>
                </div>
              </div>
            </div>
            <div className="flex justify-end px-6 pb-5 pt-1">
              <button type="button" onClick={() => setShowAbout(false)} className="inline-flex items-center justify-center box-border h-10 px-5 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition">
                Got it
              </button>
            </div>
          </Box>
        </Modal>
      )}

      <Modal
        open={openModal}
        onClose={handleClose}
        className="flex-row align-c jstfy-c"
      >
        <Box style={{ width: 760, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <h5 className="text-lg font-semibold leading-none text-ink-900">
              {startDate.isValid() ? startDate.format('ddd, D MMM YYYY') : 'Instance details'}
            </h5>
            <button type="button" onClick={handleClose} aria-label="Close" className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0">
              <X size={20} />
            </button>
          </div>

          {/* Member row */}
          {member && (
            <div className="flex items-center gap-2.5 px-6 pb-4 border-b border-rule" style={{ borderBottomStyle: 'solid' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: '#e8eef7', color: '#213d69', fontSize: 11, fontWeight: 600 }}>
                {member?.user?.profilePicture
                  ? <img src={member.user.profilePicture} alt="" className="w-full h-full object-cover" />
                  : `${member?.user?.firstName?.slice(0, 1) || ''}${member?.user?.lastName?.slice(0, 1) || ''}`.toUpperCase()}
              </div>
              <span className="text-sm font-semibold text-ink-900">{`${member?.user?.firstName || ''} ${member?.user?.lastName || ''}`.trim()}</span>
              <span className="text-ink-300">|</span>
              <span className="text-sm text-ink-500">
                {selectedInstanceData?.detectionTypes?.length > 1
                  ? `${selectedInstanceData.detectionTypes.length} Detection Types`
                  : '1 Detection Type'}
              </span>
            </div>
          )}

          {/* Body */}
          <div className="px-6 py-5">
            {selectedInstanceData?.detectionTypes && selectedInstanceData.detectionTypes.length > 1 ? (
              <div>
                <div className="flex items-center gap-1 border-b border-rule mb-4 overflow-x-auto" style={{ borderBottomStyle: 'solid' }}>
                  {selectedInstanceData.detectionTypes.map((type, index) => {
                    const active = activeTab === index;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setActiveTab(index)}
                        className={`relative inline-flex items-center px-3 py-2 text-sm bg-transparent border-0 cursor-pointer whitespace-nowrap transition ${active ? 'text-brand-700 font-semibold' : 'text-ink-500 font-medium hover:text-ink-900'}`}
                      >
                        {UnusualActivityTypes[type]?.title || type}
                        {active && <span className="absolute left-3 right-3 bottom-0" style={{ height: 2, background: '#315285', borderRadius: 2 }} />}
                      </button>
                    );
                  })}
                </div>
                {selectedInstanceData.detectionTypes.map((type, index) => (activeTab === index ? (
                  <div key={type}>
                    <h6 className="text-base font-semibold text-ink-900 mb-3">{UnusualActivityTypes[type]?.title || type}</h6>
                    {renderDetectionDetail({
                      typeKey: type,
                      description: selectedInstanceData.detectionData?.[type]?.message || UnusualActivityTypes[type]?.description || '',
                      riskLevel: selectedInstanceData.riskLevelByType?.[type] || selectedInstanceData.riskLevel,
                      confidence: selectedInstanceData.confidenceByType?.[type] || selectedInstanceData.confidence,
                      factors: selectedInstanceData.factorsByType?.[type] || [],
                    })}
                  </div>
                ) : null))}
              </div>
            ) : (
              <div>
                <h6 className="text-base font-semibold text-ink-900 mb-3">
                  {selectedInstanceData?.unusualTypeId !== undefined
                    ? (UnusualActivityTypes[selectedInstanceData.unusualTypeId]?.title || 'Unknown Activity')
                    : 'Unknown Activity'}
                </h6>
                {renderDetectionDetail({
                  typeKey: selectedInstanceData?.unusualTypeId,
                  description: selectedInstanceData?.detectionData?.[selectedInstanceData?.unusualTypeId]?.message
                    || selectedInstanceData?.detectionData?.message
                    || UnusualActivityTypes[selectedInstanceData?.unusualTypeId]?.description
                    || '',
                  riskLevel: selectedInstanceData?.riskLevel,
                  confidence: selectedInstanceData?.confidence,
                  factors: selectedInstanceData?.factors || [],
                })}
              </div>
            )}
          </div>
        </Box>
      </Modal>

      <div className="screen-wrapper">
        <div className="screen-header" style={{ fontFamily: 'Inter, sans-serif' }}>
          {/* Header — back + title (left), inputs (top-right) */}
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between mb-5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Back"
                  onClick={() => history.push('/analytics/unusual-activity')}
                  className="p-1 -ml-1 rounded text-ink-900 hover:bg-gray-100 transition flex-shrink-0"
                >
                  <ArrowLeft size={22} />
                </button>
                <h1 className="font-bold text-ink-900 leading-tight truncate text-xl lg:text-2xl" data-testid="title">
                  {memberName ? `Unusual Activity: ${memberName}` : 'Unusual Activity'}
                </h1>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <button
                type="button"
                onClick={() => setShowAbout(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-brand-700 bg-transparent border-0 cursor-pointer p-0 mr-1 transition"
              >
                <HelpCircle size={16} />
                About Unusual Activity
              </button>
              <select
                value={Number(memberId)}
                onChange={(e) => history.push(`/analytics/unusual-activity/${e.target.value}`)}
                className={STYLED_SELECT_CLASS}
                style={{ ...STYLED_SELECT_STYLE, width: 180 }}
              >
                {!members.some((m) => Number(m.memberId) === Number(memberId)) && (
                  <option value={Number(memberId)}>{memberName || 'Member'}</option>
                )}
                {members.map((m) => <option key={m.memberId} value={m.memberId}>{m.member?.user?.fullName}</option>)}
              </select>
              <div style={{ width: 290 }}>
                <DateRangePicker value={date} onChange={(_date) => setDate(_date)} />
              </div>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className={STYLED_SELECT_CLASS}
                style={{ ...STYLED_SELECT_STYLE, width: 180 }}
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>

          {/* Summary */}
          <div className="flex flex-wrap gap-3 mb-5">
            <StatCard title="Instances" value={summary.totalInstances ?? 0} subTitle="Total unusual activities" />
            <StatCard title="Total Time (hh:mm:ss)" value={moment.utc((summary.totalTime ?? 0) * 1000).format('HH:mm:ss')} />
            <StatCard title="Previous 60 days (hh:mm:ss)" value={moment.utc((summary.previousDaysTime || 0) * 1000).format('HH:mm:ss')} subTitle="Historical comparison" />
          </div>

          {/* Instances table */}
          <div className="bg-white border border-rule rounded-[12px] overflow-hidden" style={{ borderStyle: 'solid', boxShadow: '0 1px 2px rgba(14, 30, 64, 0.04)' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-rule" style={{ borderBottomStyle: 'solid' }}>
              <AlertTriangle size={16} className="text-[#CC4525]" />
              <span className="text-sm font-semibold text-ink-900">Instances</span>
            </div>
            <DataTable
              bare
              columns={columns}
              rows={instanceRows}
              getRowKey={(r) => r._idx}
              loading={isLoading}
              emptyText="No instances found."
              minWidth={900}
            />
          </div>
        </div>
      </div>

      {/* Screenshot Viewer */}
      {screenshotsAreOpen && (
        <ScreenshotViewer
          coverImage={coverImageUrl}
          screenshots={screenshots}
          memberId={memberId}
          formattedTime={formattedTime}
          setScreensAreOpen={setScreenshotsAreOpen}
          screensAreOpen={screenshotsAreOpen}
        />
      )}

      {/* Evidence Lightbox */}
      {selectedEvidenceUrl && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSelectedEvidenceUrl(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter') setSelectedEvidenceUrl(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'pointer',
          }}
        >
          <img
            src={selectedEvidenceUrl}
            alt="Evidence full view"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
            }}
          />
          <div style={{
            position: 'absolute', top: '20px', right: '20px', color: '#fff', fontSize: '24px', cursor: 'pointer',
          }}
          >
            ✕
          </div>
        </div>
      )}
    </>
  );
}

export default UnusualActivityMember;
